/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: PbxCliButtonConfigsPage.tsx
 ??? Created: 2025-01-28
 ?? Description:
    Main page for PBX CLI button configurations management with list view.
    Displays configurable button actions for PBX CLI interface.

 ?? Notes:
    - Tenant-scoped data
    - Maximum of 24 button configurations per tenant

 ?? Last Updated: 2025-11-07 by GPT-5 Codex
----------------------------------------------------------------*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Edit, Power, PowerOff, Terminal, Trash2 } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import DataGridSimple from '../../components/ui/DataGridSimple';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deletePbxCliButtonConfig, updatePbxCliButtonConfig } from './api';
import { usePbxCliButtonConfigList } from './hooks';
import { PbxCliButtonConfig } from './types';

const ACTION_BUTTON_CLASSNAMES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
  toggle: 'hover:bg-slate-100 dark:hover:bg-slate-800',
};

export default function PbxCliButtonConfigsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showError, showSuccess } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; config: PbxCliButtonConfig | null }>({
    isOpen: false,
    config: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const pageTitle = useMemo(() => t('pbxCli.buttonConfigs.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);


  const availableColumns = useMemo(() => {
    return [
      {
        key: 'label',
        label: t('pbxCli.buttonConfigs.columns.label'),
        visible: true,
        sortable: true,
      },
      {
        key: 'command',
        label: t('pbxCli.buttonConfigs.columns.command'),
        visible: true,
        sortable: true,
      },
      {
        key: 'orderIndex',
        label: t('pbxCli.buttonConfigs.columns.orderIndex'),
        visible: true,
        sortable: true,
      },
      {
        key: 'enabled',
        label: t('pbxCli.buttonConfigs.columns.enabled'),
        visible: true,
        sortable: true,
      },
      {
        key: 'tooltip',
        label: t('pbxCli.buttonConfigs.columns.tooltip'),
        visible: false,
        sortable: false,
      },
      {
        key: 'actions',
        label: t('common:actions'),
        visible: true,
        sortable: false,
      },
    ];
  }, [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = usePbxCliButtonConfigList(availableColumns);

  const handleCreate = () => {
    navigate('/pbx/tools/cli-button-configs/new');
  };

  const handleEdit = (config: PbxCliButtonConfig) => {
    navigate(`/pbx/tools/cli-button-configs/${config.uuid ?? config.id}`);
  };

  const handleDuplicate = (config: PbxCliButtonConfig) => {
    navigate(`/pbx/tools/cli-button-configs/new?duplicate=${config.uuid ?? config.id}`);
  };

  const handleToggleEnabled = useCallback(
    async (config: PbxCliButtonConfig) => {
      try {
        await updatePbxCliButtonConfig(config.id, {
          label: config.label,
          command: config.command,
          enabled: !config.enabled,
        });

        showSuccess(
          config.enabled
            ? t('pbxCli.buttonConfigs.messages.disableSuccess', { label: config.label })
            : t('pbxCli.buttonConfigs.messages.enableSuccess', { label: config.label }),
        );
        refresh();
      } catch (error) {
        showError(error.message || t('pbxCli.buttonConfigs.messages.toggleError'));
      }
    },
    [refresh, showError, showSuccess, t],
  );

  const handleDeleteClick = (config: PbxCliButtonConfig) => {
    setDeleteModal({ isOpen: true, config });
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.config) return;

    setIsDeleting(true);
    try {
      await deletePbxCliButtonConfig(deleteModal.config.id);

      showSuccess(t('pbxCli.buttonConfigs.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, config: null });
      refresh();
    } catch (error) {
      showError(error.message || t('pbxCli.buttonConfigs.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.config, refresh, showError, showSuccess, t]);

  const renderActions = useCallback(
    (config: PbxCliButtonConfig) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip
          content={
            config.enabled
              ? t('pbxCli.buttonConfigs.actions.disable')
              : t('pbxCli.buttonConfigs.actions.enable')
          }
          position="top"
        >
          <button
            type="button"
            onClick={() => handleToggleEnabled(config)}
            aria-label={
              config.enabled
                ? t('pbxCli.buttonConfigs.actions.disable')
                : t('pbxCli.buttonConfigs.actions.enable')
            }
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.toggle}`}
          >
            {config.enabled ? (
              <PowerOff className="h-4 w-4 text-slate-600 dark:text-slate-400" />
            ) : (
              <Power className="h-4 w-4 text-slate-600 dark:text-slate-400" />
            )}
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(config)}
            aria-label={t('common:edit')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.edit}`}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(config)}
            aria-label={t('common:duplicate')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.duplicate}`}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:delete')} position="top">
          <button
            type="button"
            onClick={() => handleDeleteClick(config)}
            aria-label={t('common:delete')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.delete}`}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, handleToggleEnabled, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns.map((column) => ({
            ...column,
            render:
              column.key === 'label'
                ? (_value, item: PbxCliButtonConfig) => (
                    <span className="font-medium text-foreground">{item.label}</span>
                  )
                : column.key === 'command'
                ? (_value, item: PbxCliButtonConfig) => (
                    <span className="font-mono text-xs text-muted-foreground">{item.command}</span>
                  )
                : column.key === 'orderIndex'
                ? (_value, item: PbxCliButtonConfig) => (
                    <span className="text-sm text-muted-foreground">{item.orderIndex}</span>
                  )
                : column.key === 'enabled'
                ? (_value, item: PbxCliButtonConfig) => (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        item.enabled
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                      }`}
                    >
                      {item.enabled ? t('common:enabled') : t('common:disabled')}
                    </span>
                  )
                : column.key === 'tooltip'
                ? (_value, item: PbxCliButtonConfig) => (
                    <span className="text-sm text-muted-foreground">{item.tooltip || t('common:notAvailable')}</span>
                  )
                : column.key === 'actions'
                ? (_value, item: PbxCliButtonConfig) => renderActions(item)
                : column.render,
          }))}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          onAdd={handleCreate}
          title={pageTitle}
          titleIcon={<Terminal className="h-5 w-5 text-primary" />}
          addButtonText={t('pbxCli.buttonConfigs.actions.add')}
          searchPlaceholder={t('pbxCli.buttonConfigs.searchPlaceholder')}
          emptyMessage={t('pbxCli.buttonConfigs.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, config: null })}
        onConfirm={handleDeleteConfirm}
        title={t('pbxCli.buttonConfigs.messages.deleteTitle')}
        message={t('pbxCli.buttonConfigs.messages.deleteConfirmation', {
          label: deleteModal.config?.label ?? '',
        })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

